import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import React from 'react';
import { QosErrorBoundary } from './QosErrorBoundary';
import { IQosContextProps, QosContext } from './QosContext';
import { createQosTelemetryReporter } from './createQosTelemetryReporter';
import { IComponent, QosState } from './types';
import { useQos } from '.';
import { useTelemetry } from '..';

/**
 * Timestamp for QosProvider to timeout
 */
export const timeoutMs = 30000;

export interface IQosProviderProps {
  /**
   * Name to represent the content that is wrapped by QosProvider
   */
  readonly name: string;
  /**
   * Customizable time in ms for the QosProvider to timeout
   */
  readonly timeout?: number;
  /**
   * Additional callback when the Qos state changes.
   */
  readonly onQosStateChange?: (state: QosState) => void;
}

/**
 * Wrapper component that wraps the target UI to track its QoS. The target UI can be a single component or collection of components
 */
export const QosProvider: React.FC<IQosProviderProps> = ({
  children,
  name: qosProviderName,
  timeout = timeoutMs,
  onQosStateChange
}) => {
  const incompleteComponents = useRef(new Set<IComponent>());
  const [state, setState] = useState<QosState>(QosState.IDLE);
  const [componentCount, setComponentCount] = useState(0);
  const telemetry = useTelemetry();
  // Get access to the context from the parent QosProvider because the current QosProvider is also a child of its parent, so it needs to report its own QoS status.
  // If this is the outermost QosProvider i.e. there is no context from the parent, then the returned callbacks are no-op.
  const parentQosContext = useQos(qosProviderName, { disableWithoutContext: true });
  const qosTelemetryReporter = useMemo(() => {
    return createQosTelemetryReporter(telemetry, qosProviderName);
  }, [telemetry, qosProviderName]);

  const currentQosContext: IQosContextProps = useMemo(() => {
    function unregister(child: IComponent) {
      incompleteComponents.current.delete(child);
      setComponentCount(incompleteComponents.current.size);
    }

    return {
      name: qosProviderName,
      unregisterComponent(child) {
        unregister(child);
      },
      registerComponent(child) {
        incompleteComponents.current.add(child);
        setComponentCount(incompleteComponents.current.size);
      },
      reportLoadComplete(child) {
        unregister(child);
      },
      reportFailure(child, error) {
        unregister(child);
        qosTelemetryReporter.failure(error, `${qosProviderName}/${child.componentName}`);
        setState(qosTelemetryReporter.state);
      }
    };
  }, [qosTelemetryReporter, qosProviderName]);

  useEffect(() => {
    if (incompleteComponents.current.size === 0) {
      // Send Qos log
      qosTelemetryReporter.complete();
      setState(qosTelemetryReporter.state);
      // Reports complete to the parent QosProvider
      parentQosContext && parentQosContext.reportLoadComplete();
    }
  }, [qosTelemetryReporter, componentCount, parentQosContext]);

  // Timeout after `timeout` milliseconds have elapsed. The timeout will reset
  // if the QoS context or duration changes.
  useEffect(() => {
    const timeoutCreator = setTimeout(() => {
      if (incompleteComponents.current.size > 0) {
        qosTelemetryReporter.timeout();
        setState(qosTelemetryReporter.state);
        // Always report complete to the parent as timeout and failure only affect the current QosProvider
        parentQosContext && parentQosContext.reportLoadComplete();
      }
    }, timeout);

    return () => clearTimeout(timeoutCreator);
  }, [qosTelemetryReporter, parentQosContext, timeout]);

  useEffect(() => {
    if (state !== QosState.IDLE) {
      onQosStateChange?.(state);
    }
  }, [state, onQosStateChange]);

  const onCatchError = useCallback(
    (error: unknown) => {
      // Send QoS log immediately so we won't lose it before the QosProvider unmounts
      qosTelemetryReporter.failure(error);
      setState(qosTelemetryReporter.state);
    },
    [qosTelemetryReporter]
  );

  return (
    <QosContext.Provider value={currentQosContext}>
      <QosErrorBoundary onCatch={onCatchError}>{children}</QosErrorBoundary>
    </QosContext.Provider>
  );
};
